/**
 * Kart — **uzuv** mertebesi: kendi başına anlamlı bir blok.
 *
 * Kurallar (docs/13-TASARIM-SISTEMI.md bölüm 4):
 *   - Kendi `margin`'ini YAZMAZ; aralığı onu yerleştiren kap `gap` ile verir.
 *   - Kendi genişliğini dayatmaz; kabı ne verirse ona uyar.
 *   - İç dolgu **24px** = kartlar arası boşluk (24px) ile aynı üst sınırda;
 *     daha büyük olsa gruplar birbirine karışırdı (yakınlık ilkesi, doküman 2.4).
 *     Ölçü Vuexy 10.11'den alındı (`$card-spacer: $spacer * 1.5`) — 16px'lik
 *     dolgu kartı sıkışık gösteriyordu.
 *   - Yarıçap `--radius-lg` (14px) — uzuv mertebesi. İçindeki her şey daha
 *     küçük yarıçap kullanır.
 *   - `.kap` sınıfı: içindeki bileşenler ekranı değil **bu kabı** ölçer.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface KartProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function Kart({ className, children, ...kalan }: KartProps) {
  return (
    <section
      // `.uzuv` kart yüzeyini tek yerden verir (zemin + yarıçap + gölge).
      // Kenarlık YOK: ölçüldü (Vuexy `$card-border-width: 0`) — kenarlıklı
      // kartlar yan yana dizilince ekran kafes görünüyor.
      className={cn('kap uzuv', className)}
      {...kalan}
    >
      {children}
    </section>
  );
}

export interface KartBasligiProps {
  baslik: string;
  /** Başlığın altındaki tek satır açıklama. */
  aciklama?: string;
  /** Sağa yaslanan eylemler — kart başlığı bir "el"dir. */
  eylemler?: ReactNode;
}

export function KartBasligi({ baslik, aciklama, eylemler }: KartBasligiProps) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 px-6 pt-6 pb-0">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="text-lg font-semibold text-[var(--ink)]">{baslik}</h2>
        {aciklama && <p className="text-sm text-[var(--ink-muted)]">{aciklama}</p>}
      </div>
      {eylemler && <div className="flex shrink-0 items-center gap-2">{eylemler}</div>}
    </header>
  );
}

/**
 * Kart gövdesi — dolgu tek yerden gelsin diye ayrı bileşen.
 * Başlığın hemen ardında geliyorsa üst dolgu 16px'e düşer (styles.css
 * `.uzuv > header + *`): başlık ve gövde tek blok okunur, çizgi yok (Vuexy).
 */
export function KartGovdesi({ className, children, ...kalan }: KartProps) {
  return (
    <div className={cn('p-6', className)} {...kalan}>
      {children}
    </div>
  );
}
